import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { addToCollectionsCache } from '../../../state/actions/collectionsCacheActions';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import axios from 'axios';
import Layout from '../../../layout/Layout';
import CollectionPage3D from '../../../components/collections/CollectionPage3D';
import { ENV } from '../../../constants/environments';
import { getSession } from 'next-auth/react';
import { SessionState } from '../../../types/state/reducers/authReducersTypes';
import cache from '../../../helpers/redis';
import errorRedirectHandler from '../../../helpers/errorRedirectHandler';
import { setCurrentProduct, clearCurrentProduct } from '../../../state/actions/eventTrackingActions';
import ProductPageVisitPlugin from '../../../components/user-interest-analytics/ProductPageVisitPlugin';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../types/state/storeTypes';

const ProductPage3D: React.FC = (props: any) => {
  const dispatch = useDispatch();
  const headContent = {
    title: props.collection.name + ' 3D Models',
    keywords: [
      '3d assets',
      'actionvfx',
      '3d models',
      '3d effects',
      '3d fx',
      'vdbs',
      'animated vdbs',
      'animated 3d models'
    ].concat(props.collection.keywords),
    description: props.collection.short_description,
    og: {
      title: props.collection.name + ' 3D Models',
      image: props.collection.poster
    }
  };

  const user = useSelector((state: ApplicationState) => state.auth.user);
  const subscription = useSelector((state: ApplicationState) => state.auth.subscription);
  const auth = useSelector((state: ApplicationState) => state.auth);

  useEffect(() => {
    if (!props.collection) return;
    // this page uses server side cache, but
    // store in client-side cache for e.g. wishlists
    dispatch(addToCollectionsCache(props.collection));
    dispatch(setCurrentProduct(props.collection));

    return () => {
      dispatch(clearCurrentProduct());
    };
  }, [props.collection]);

  return (
    <Layout head={headContent}>
      <CollectionPage3D
        collection={props.collection}
        products={props.products}
        product={props.product}
        ownership={props.ownership}
        user={user}
        subscription={subscription}
        auth={auth}
      />
      <ProductPageVisitPlugin product={props.collection} />
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx: GetServerSidePropsContext) => {
  ctx.res.setHeader('Cache-Control', 'no-store, no-cache, must-revalidate, proxy-revalidate');
  const session: SessionState = await getSession(ctx);
  const apiUrl: string = `${ENV.api.baseURL}`;

  const getCollection = async () => {
    const collectionsUrl: string = `${apiUrl}/collections/${ctx.params.slug}?library_id=3`;
    const collectionParams = {};
    const collectionsRes = await axios.get(collectionsUrl, {
      params: collectionParams,
      headers: {
        'Content-Type': 'application/json',
        authorization: session?.user?.token
      }
    });
    return collectionsRes?.data;
  }

  let collection: any;
  try {
    collection = await getCollection();
  }
  catch (error) {
    return errorRedirectHandler(error);
  }

  if (collection?.library?.id === 2) {
    return {
      redirect: {
        destination: `/practice-footage/${collection.slug}`,
        permanent: false,
      }
    }
  }

  if (collection?.library?.id === 1) {
    return {
      redirect: {
        destination: `/collections/${collection.slug}`,
        permanent: false,
      }
    }
  }

  const getProducts = async () => {
    return await cache(
      `avfx_collection_products_1_${ctx.params.slug}_${session?.user?.id}_${session?.subscription?.id}`,
      3600,
      async () => {
        const productsRes = await axios.get(`${apiUrl}/collections/${collection.id}/products`, {
          headers: {
            'Content-Type': 'application/json',
            authorization: session?.user?.token
          }
        });
        return productsRes?.data;
      }
    );
  }

  let products: any;
  try {
    products = await getProducts();
  }
  catch (error) {
    return errorRedirectHandler(error);
  }

  let downloads = null;
  if (session?.user?.token) {
    const downloadsUrl: string = `${apiUrl}/products/${collection.id}/ownership`;
    let downloadsRes: any;
    try {
      downloadsRes = await axios.get(downloadsUrl, {
        headers: {
          authorization: session.user.token
        }
      });
    }
    catch (error) {
      return errorRedirectHandler(error);
    }
    downloads = downloadsRes?.data;
  }

  return {
    props: {
      collection: collection,
      products: products,
      ownership: downloads?.data || {},
      product: products.find((p) => p.id == ctx.query.id) || null
    }
  };
};

export default ProductPage3D;
